import { ApyChart } from 'app/features/silo/charts/ApyChart';
import { ChartDateRange } from 'app/features/silo/charts/ChartRangeToggle';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import { pipe } from 'fp-ts/lib/function';
import { toEntries } from 'fp-ts/lib/ReadonlyRecord';
import { ComponentProps, useMemo } from 'react';
import { match } from 'ts-pattern';
import { MarketInterestRates } from './graph';
import { ChecksumAddress, ProtocolInstance } from '@silo/types';
import { isUndefined } from '@silo/utils';
import { adjustDepositAprNum } from '@silo/contract-utils';
import { useSiloWeb3 } from '@silo/web3';

type DailyRateTimeseries = MarketInterestRates['rates'][number]['interestRateDaily'][number];
type HourlyRateTimeseries = MarketInterestRates['rates'][number]['interestRateHourly'][number];
/**
 * "number" key is a unix timestamp
 */
type ByDay = Record<number, Omit<MarketInterestRates['rates'][number]['interestRateDaily'][number], 'day'> | undefined>;
type ByHour = Record<
  number,
  Omit<MarketInterestRates['rates'][number]['interestRateHourly'][number], 'hour'> | undefined
>;
type BySide = Record<MarketInterestRates['rates'][number]['side'], { daily: ByDay; hourly: ByHour } | undefined>;
type ByAsset = Record<ChecksumAddress, BySide | undefined>;

type ChartData = ComponentProps<typeof ApyChart>['data'];
type ChartDataByAsset = Record<ChecksumAddress, { daily: ChartData; hourly: ChartData } | undefined>;

/**
 * Maps data from the graph query to a format that can be used by the APY chart
 */
export const useInterestRatesChartData = <T extends MarketInterestRates | undefined>(
  interestRates: T
): T extends MarketInterestRates ? ChartDataByAsset : undefined => {
  const { protocolInstance } = useSiloWeb3();

  const byAsset = useMemo(() => {
    if (isUndefined(interestRates) || isUndefined(protocolInstance)) {
      return undefined;
    }

    return pipe(interestRatesByAsset(interestRates.rates), (i) => toChartData(i, protocolInstance));
  }, [interestRates, protocolInstance]);

  return byAsset as T extends MarketInterestRates ? ChartDataByAsset : undefined;
};

export const useChartDataForDayRangeAndAsset = <T extends ChartDataByAsset | undefined>(
  byAsset: T,
  asset: ChecksumAddress,
  dayRange: ChartDateRange
) => {
  return useMemo(() => {
    const data = byAsset?.[asset];

    if (!data) return undefined;

    return match(dayRange)
      .with(30, () => data.daily.slice(1, 31))
      .with(7, () => data.daily.slice(1, 8))
      .with(1, () => data.hourly.slice(0, 24))
      .exhaustive()
      .reverse();
  }, [byAsset, asset, dayRange]) as T extends ChartDataByAsset ? ChartData : undefined;
};

const interestRatesByAsset = (interestRates: MarketInterestRates['rates']) => {
  return interestRates.reduce((acc, rate) => {
    const daily = byDay(rate.interestRateDaily);
    const hourly = byHour(rate.interestRateHourly);
    let asset = acc[rate.token.id];

    if (!asset) {
      asset = {
        LENDER: undefined,
        BORROWER: undefined,
      };
    }

    asset[rate.side] = { daily, hourly };
    acc[rate.token.id] = asset;

    return acc;
  }, {} as ByAsset);
};

const byDay = (rates: DailyRateTimeseries[]) => {
  return rates.reduce((acc, rate) => {
    acc[getUnixTime(rate.day)] = deleteKey('day', rate);

    return acc;
  }, {} as ByDay);
};

const byHour = (rates: HourlyRateTimeseries[]) => {
  return rates.reduce((acc, rate) => {
    acc[getUnixTime(rate.hour)] = deleteKey('hour', rate);

    return acc;
  }, {} as ByHour);
};

const deleteKey = <T extends object, K extends keyof T>(key: K, obj: T): Omit<T, K> => {
  const { [key]: _, ...rest } = obj;
  return rest;
};

const toChartData = (byAsset: ByAsset, protocolInstance: ProtocolInstance): ChartDataByAsset => {
  return toEntries(byAsset).reduce((acc, [assetAddress, bySide]) => {
    if (!bySide) return acc;

    if (!bySide.BORROWER) return acc;

    const daily = toEntries(bySide.BORROWER.daily)
      .map(([timestampString, rate]) => {
        const timestamp = Number(timestampString);
        const borrowApy = rate?.rateAvg ?? 0;
        const rawDepositApy = bySide.LENDER?.daily[timestamp]?.rateAvg ?? 0;
        const depositApy = adjustDepositAprNum(rawDepositApy, protocolInstance);
        const date = fromUnixTime(timestamp);
        // Remove timezone offset to make sure the date is displayed consistently no matter the timezone of the user
        // This is safe, because the date is created from a unix timestamp, which is always in UTC
        const noTimezone = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
        return {
          depositApy,
          borrowApy,
          date: noTimezone,
        };
      })
      .sort((a, b) => b.date.getTime() - a.date.getTime());

    const hourly = toEntries(bySide.BORROWER.hourly)
      .map(([timestampString, rate]) => {
        const timestamp = Number(timestampString);
        const borrowApy = rate?.rateAvg ?? 0;
        const rawDepositApy = bySide.LENDER?.hourly[timestamp]?.rateAvg ?? 0;
        const depositApy = adjustDepositAprNum(rawDepositApy, protocolInstance);
        const date = fromUnixTime(timestamp);
        return {
          depositApy,
          borrowApy,
          date,
        };
      })
      .sort((a, b) => b.date.getTime() - a.date.getTime());

    acc[assetAddress] = { daily, hourly };

    return acc;
  }, {} as ChartDataByAsset);
};
